import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { asMemberRole } from '@/lib/mcMembership';

/**
 * Task 11: Mitglieder-Zeile eines Merchant Centers, wie sie die RPC
 * `list_merchant_center_members(p_mc_id uuid)` zurueckgibt (Migration
 * `20260923110000_merchant_center_members.sql`). NICHT dasselbe wie
 * `McMemberRow` aus `lib/mcMembership.ts` -- das ist die ROHE Tabellenzeile
 * der Basistabelle (traegt `id`/`access_status`, keine `email`); `McMember`
 * hier ist das aufgeloeste RPC-Ergebnis mit E-Mail-Adresse fuer die Anzeige.
 * Absichtlich zwei Typen an zwei Stellen, siehe die Begruendung am Kopf von
 * `McMemberRow` in `mcMembership.ts`.
 */
export interface McMember {
  user_id: string;
  role: 'owner' | 'member';
  email: string;
  joined_at: string;
  google_account_id: string;
}

/**
 * Laedt die Mitgliederliste einer Merchant Center ueber die
 * SECURITY-DEFINER-RPC `list_merchant_center_members` (Migration
 * `20260923110000_merchant_center_members.sql`) -- die Funktion prueft
 * intern selbst, ob der Aufrufer Mitglied dieser MC ist, und loest die
 * E-Mail-Adresse jedes Mitglieds auf (RLS auf `auth.users` erlaubt das ueber
 * die Data API sonst nicht). Der generierte Typ fuehrt `role` als `string`;
 * `asMemberRole` engt die Spalte (CHECK-Constraint auf genau zwei Werte) auf
 * `McMember['role']` ein, eine Zeile mit unbekannter Rolle faellt heraus.
 *
 * `enabled: !!mcId` haelt die Query passiv, solange keine MC ausgewaehlt ist
 * -- `McMembersCard` wird nur mit einer konkreten `mc.id` gemountet, aber der
 * Parameter bleibt nullable, damit ein Aufrufer ohne Auswahl nicht selbst
 * bedingt rendern muss.
 */
export function useMcMembers(mcId: string | null) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['mc-members', mcId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_merchant_center_members', {
        p_mc_id: mcId as string,
      });
      if (error) throw error;
      return (data ?? []).flatMap((row): McMember[] => {
        const role = asMemberRole(row.role);
        return role ? [{ ...row, role }] : [];
      });
    },
    enabled: !!mcId,
  });

  return { members: data ?? [], isLoading, isError, refetch };
}
